import ReactGA from 'react-ga4';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink } from 'react-router-dom';
import { useState } from 'react';

import { AppState } from 'types';

import { resetIntake } from 'redux/actions/intake';
import { resetDebitCardInfo } from 'redux/actions/debit_card';
import { openPopUp } from 'redux/store/slices/eligibilitySlice';

import { makeStyles } from '@mui/styles';

const useStyles = makeStyles(() => ({
    listItemText: {
        borderRadius: '8px',
        color: '#FFFFFF',
        fontSize: '18px',
        fontWeight: 500,
        lineHeight: '20px',
        padding: '18px 0',
        textDecoration: 'none',
        whiteSpace: 'pre-wrap',
        width: '100%',
        display: 'block',
        textAlign: 'left',
        '&:hover': {
            background: 'rgba(0, 0, 0, 0.08)'
        },
        '&:focus': {
            outline: '2px solid #FFFFFF',
            outlineOffset: '2px'
        },
        '&:focus:not(:focus-visible)': {
            outline: 'none'
        },
        '&:focus-visible': {
            outline: '2px solid #FFFFFF',
            outlineOffset: '2px'
        },
        '& > i': {
            fontSize: '20px',
            margin: '0 15px'
        },
        '&.active': {
            backgroundColor: '#7378E8',
            color: 'white',
            padding: '18px 0',
            textDecoration: 'none',
            transition: 'all 0.3s ease-in-out'
        }
    },
    listItemTextDisabled: {
        opacity: 0.5,
        '&:focus': {
            outline: '2px solid #FFFFFF',
            outlineOffset: '2px'
        }
    }
}));

type NavItemProps = {
    setEligibilityPopUpOpen?: boolean;
    title: string;
    to: string;
    newTab?: boolean;
    disabled?: boolean;
    disabledText?: string;
};

export default function NavItem({
    to,
    title,
    newTab = false,
    disabled = false,
    disabledText = ''
}: NavItemProps) {
    const classes = useStyles();
    const dispatch = useDispatch();
    const [hoveredItem, setHoveredItem] = useState<boolean>(false);
    const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
    
    const isEligibilityPopUpOpen = useSelector(
        (state: AppState) => state.eligibility.isPopUpOpen
    );

    const navIcons = {
        'Client List': 'far fa-users',
        'Contact Support': 'far fa-envelope',
        FAQ: 'far fa-question-circle',
        Logout: 'far fa-sign-out',
        'Member Sign Up': 'far fa-user-plus',
        'Your Referrals': 'far fa-user-friends',
        Settings: 'far fa-cog'
    } as Record<string, string>;

    const handleMouseMove = (e: React.MouseEvent) => {
        setMousePosition({ x: e.clientX, y: e.clientY });
    };

    const handleGAClick = (title: string) => {
        if (disabled) {
            return;
        }

        ReactGA.event({
            category: 'Navigation',
            action: 'Click',
            label: title
        });

        dispatch(resetDebitCardInfo());
        dispatch(resetIntake());

        if (
            title === 'Member Sign Up'
        ) {
            dispatch(openPopUp());
        }

        if (title === 'Contact Support') {
            window.open('mailto:support@samaritan.city');
        }
    };

    const getAriaLabel = (): string => {
        if (disabled && disabledText) {
            return `${title}. ${disabledText}`;
        }
        
        const destinationMap: Record<string, string> = {
            'Client List': 'Navigate to Member List',
            'Member Sign Up': 'Navigate to Member Registration',
            'Your Referrals': 'Navigate to Your Referrals',
            'FAQ': 'Open FAQ page in new tab',
            'Settings': 'Navigate to Settings page',
            'Contact Support': 'Open email to contact support',
            'Logout': 'Navigate to logout page'
        };

        const baseLabel = destinationMap[title] || `Navigate to ${title}`;
        
        if (newTab) {
            return `${baseLabel} (opens in new tab)`;
        }
        
        return baseLabel;
    };

    return (
        <>
            <NavLink
                to={disabled ? '#' : { pathname: to }}
                target={newTab ? '_blank' : undefined}
                rel={newTab ? 'noopener noreferrer' : undefined}
                className={`${classes.listItemText} ${disabled ? classes.listItemTextDisabled : ''}`}
                activeClassName={`${classes.listItemText} active`}
                aria-label={getAriaLabel()}
                aria-disabled={disabled}
                tabIndex={disabled ? -1 : 0}
                isActive={(match, location) => {
                    if (!match || disabled) {
                        return false;
                    }

                    if (location.pathname.includes('holders/')) {
                        return false;
                    }

                    if (
                        location.pathname === '/holders' &&
                        title === 'Client List' &&
                        isEligibilityPopUpOpen
                    ) {
                        return false;
                    }

                    if (
                        location.pathname === '/holders' &&
                        title === 'Member Sign Up' &&
                        !isEligibilityPopUpOpen
                    ) {
                        return false;
                    }

                    if (
                        title === 'Member Sign Up' &&
                        !isEligibilityPopUpOpen
                    ) {
                        return false;
                    }

                    return true;
                }}
                onClick={() => handleGAClick(title)}
                onMouseEnter={() => {
                    if (disabled) {
                        setHoveredItem(true);
                    }
                }}
                onMouseLeave={() => {
                    setHoveredItem(false);
                }}
                onMouseMove={handleMouseMove}
            >
                <i className={`${navIcons[title]}`} aria-hidden="true" />
                {title}
            </NavLink>
            
            {/* Tooltip for disabled items */}
            {hoveredItem && disabled && disabledText && (
                <div
                    style={{
                        position: 'fixed',
                        left: mousePosition.x + 15,
                        top: mousePosition.y - 30,
                        zIndex: 9999,
                        pointerEvents: 'none'
                    }}
                >
                    <div
                        style={{
                            backgroundColor: 'white',
                            color: '#1F2152',
                            padding: '12px 16px',
                            borderRadius: '10px',
                            border: '1px solid #E3E3FA',
                            maxWidth: '300px',
                            fontSize: '14px',
                            fontWeight: 500,
                            lineHeight: '1.5',
                            position: 'relative'
                        }}
                    >
                        {disabledText}
                    </div>
                </div>
            )}
        </>
    );
}
